import { useCallback, useEffect, useMemo, useState } from 'react';

import { useStoreContext } from 'features/store';
import { isEqual, unionBy } from 'lodash-es';

import { ILegacyCartEntry, IServerOrder } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useMenuContext } from 'state/menu';
import { getUnavailableItemsFromServerOrder } from 'state/order/utils';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isCartEntryAvailableOneMenu } from 'utils/availability';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';

type IUseUnavailableCartEntriesHook = (args: {
  serverOrder: IServerOrder | null;
  cartEntries: ILegacyCartEntry[];
  isStoreChangeHydrationInProgress?: boolean;
}) => {
  unavailableCartEntries: ILegacyCartEntry[];
  setUnavailableCartEntries: React.Dispatch<React.SetStateAction<ILegacyCartEntry[]>>;
};

export const useUnavailableCartEntries: IUseUnavailableCartEntriesHook = ({
  cartEntries,
  serverOrder,
  isStoreChangeHydrationInProgress = false,
}) => {
  const { isMenuLoading: isLegacyMenuLoading } = useMenuContext();
  const [unavailableMenuCartEntries, setUnavailableMenuCartEntries] = useState<ILegacyCartEntry[]>(
    []
  );
  const [unavailableCartEntries, setUnavailableCartEntries] = useState<ILegacyCartEntry[]>(
    getUnavailableItemsFromServerOrder({ cartEntries, serverOrder })
  );

  const unavailableMenuCartEntriesIds = useMemo(
    () => new Set(unavailableMenuCartEntries.map(({ _id }) => _id)),
    [unavailableMenuCartEntries]
  );

  const { checkItemAvailability } = useMenuContext();

  const getUnavailableItemsFromMenu = useCallback(
    async (cartEntries: ILegacyCartEntry[]) => {
      if (isLegacyMenuLoading || isStoreChangeHydrationInProgress) {
        return { items: [], newUnavailableItems: false };
      }

      const items: ILegacyCartEntry[] = [];
      let newUnavailableItems = false;

      for (const cartEntry of cartEntries) {
        const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);
        if (!itemId) {
          continue;
        }

        const { availabilityStatus } = await checkItemAvailability(itemId);

        if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
          items.push(cartEntry);
          // If the unavailable entry does not exist in unavailableMenuCartEntriesIds
          // then we need to update state
          newUnavailableItems =
            newUnavailableItems || !unavailableMenuCartEntriesIds.has(cartEntry._id);
        }
      }

      return {
        items,
        newUnavailableItems,
      };
    },
    [
      checkItemAvailability,
      isLegacyMenuLoading,
      isStoreChangeHydrationInProgress,
      unavailableMenuCartEntriesIds,
    ]
  );

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { posServiceMode } = useMenuQueryOptions();
  const { store } = useStoreContext();
  const {
    indexedMenu,
    isLoading: isOneMenuLoading,
    loadedStoreNumber,
    hasError: unifiedMenuHasError,
  } = useUnifiedMenuContext();
  const isUnifiedMenuLoaded = indexedMenu.isLoaded();
  const isCurrentStoreMenuFetched = Boolean(store.number) && loadedStoreNumber === store.number;
  const isCurrentStoreUnifiedMenuLoaded = isCurrentStoreMenuFetched && isUnifiedMenuLoaded;
  const shouldUseOneMenuAvailability = isUnifiedMenuEnabled && isCurrentStoreUnifiedMenuLoaded;
  const shouldWaitForOneMenuAvailability =
    Boolean(store.number) &&
    isUnifiedMenuEnabled &&
    !unifiedMenuHasError &&
    !isCurrentStoreMenuFetched &&
    (isOneMenuLoading || loadedStoreNumber !== store.number);
  const { activeDayParts } = useMenuContext();
  const dayPartIds = useMemo(() => activeDayParts.map(dayPart => dayPart.key), [activeDayParts]);

  const getUnavailableItemsFromOneMenu = useCallback(
    (cartEntries: ILegacyCartEntry[]) => {
      if (isOneMenuLoading || isStoreChangeHydrationInProgress) {
        return { items: [], newUnavailableItems: false };
      }

      const items = cartEntries.filter(
        (cartEntry: ILegacyCartEntry) =>
          !isCartEntryAvailableOneMenu(cartEntry, indexedMenu, posServiceMode, dayPartIds)
      );
      return Promise.resolve({
        items,
        newUnavailableItems: items.some(entry => !unavailableMenuCartEntriesIds.has(entry._id)),
      });
    },
    [
      isOneMenuLoading,
      isStoreChangeHydrationInProgress,
      indexedMenu,
      posServiceMode,
      dayPartIds,
      unavailableMenuCartEntriesIds,
    ]
  );

  // set unavailable cart entries based on serverOrder
  useEffect(() => {
    if (!serverOrder || isStoreChangeHydrationInProgress) {
      return;
    }
    setUnavailableCartEntries(oldEntries => {
      const newEntries = unionBy(
        getUnavailableItemsFromServerOrder({ cartEntries, serverOrder }),
        oldEntries,
        'cartId'
      );

      return isEqual(newEntries, oldEntries) ? oldEntries : newEntries;
    });
  }, [cartEntries, isStoreChangeHydrationInProgress, serverOrder]);

  // Check menu for unavailable cartEntries
  useEffect(() => {
    let mounted = true;
    async function evaluateMenuAvailability() {
      const method = shouldUseOneMenuAvailability
        ? getUnavailableItemsFromOneMenu
        : getUnavailableItemsFromMenu;

      const { items, newUnavailableItems } = await method(cartEntries);

      // If the unavailable items array length has changed this may indicate an unavailable item have been removed
      const lengthChanged = items.length !== unavailableMenuCartEntries.length;
      const shouldUpdate = newUnavailableItems || lengthChanged;
      if (mounted && shouldUpdate) {
        setUnavailableMenuCartEntries(items);
      }
    }

    if (shouldWaitForOneMenuAvailability) {
      return;
    }

    if (cartEntries?.length && serverOrder) {
      evaluateMenuAvailability();
    }

    return () => {
      mounted = false;
    };
  }, [
    cartEntries,
    shouldUseOneMenuAvailability,
    shouldWaitForOneMenuAvailability,
    serverOrder,
    getUnavailableItemsFromMenu,
    getUnavailableItemsFromOneMenu,
    unavailableMenuCartEntries.length,
  ]);

  // If all cart entries were removed, clean up the unavailableMenuCartEntries state
  useEffect(() => {
    let mounted = true;
    if (!cartEntries?.length && unavailableMenuCartEntries?.length && mounted) {
      setUnavailableMenuCartEntries([]);
    }
    return () => {
      mounted = false;
    };
  }, [cartEntries?.length, unavailableMenuCartEntries?.length]);

  return useMemo(
    () => ({
      unavailableCartEntries: unionBy(unavailableCartEntries, unavailableMenuCartEntries, 'cartId'),
      setUnavailableCartEntries,
    }),
    [unavailableCartEntries, unavailableMenuCartEntries]
  );
};
